<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { FileText } from 'lucide-vue-next'
import { reactive, ref } from 'vue'

import DialogLoadingOverlay from '@/components/DialogLoadingOverlay.vue'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { generatePsaLoanApplicationDocumentsPath } from '@/routes'

const INCOME_DOCS = [
  'Recent Payslips',
  'Bank Statements to verify salary credits',
  'PAYG / Income Payment Summaries',
  'Rental Statement / Rental Agreement',
  'Centrelink Detailed Income & Asset Statement',
  'Evidence of Bonuses / Commission',
  'Employment Contract / Letter',
  'Tax returns / Financial plus ATO assessments',
  'Letter from accountant',
  'Business Activity statements',
  'Business Account statements',
] as const

const LIABILITY_DOCS = [
  'Equifax Credit Report',
  'Bank statements',
  'Loan account statement(s)',
  'Credit card statement(s)',
  'Rental Ledger / Rental Agreement',
] as const

interface Props {
  applicationId: string
}

const props = defineProps<Props>()

const open = ref(false)
const processing = ref(false)
const income = reactive<Record<string, boolean>>({})
const liability = reactive<Record<string, boolean>>({})

const selectedIncome = () => INCOME_DOCS.filter((d) => income[d])
const selectedLiability = () => LIABILITY_DOCS.filter((d) => liability[d])
const hasSelection = () => selectedIncome().length > 0 && selectedLiability().length > 0

function reset() {
  INCOME_DOCS.forEach((d) => delete income[d])
  LIABILITY_DOCS.forEach((d) => delete liability[d])
}

function submit() {
  if (!hasSelection() || processing.value) return
  processing.value = true

  router.post(
    generatePsaLoanApplicationDocumentsPath(props.applicationId),
    { income_docs: selectedIncome(), liability_docs: selectedLiability() },
    {
      preserveScroll: true,
      onSuccess: () => { open.value = false; reset() },
      onFinish: () => { processing.value = false },
    },
  )
}
</script>

<template>
  <Dialog :open="open" @update:open="(v) => { if (!processing) open = v }">
    <DialogTrigger as-child>
      <Button size="sm" variant="outline" class="w-full justify-start gap-2">
        <FileText class="size-3.5" />
        Generate PSA
      </Button>
    </DialogTrigger>
    <DialogContent class="max-w-md">
      <DialogLoadingOverlay :show="processing" message="Generating PSA…" />
      <DialogHeader>
        <DialogTitle>Generate Preliminary Credit Assessment</DialogTitle>
      </DialogHeader>

      <div class="space-y-4 py-2">
        <!-- Income documents -->
        <div class="space-y-2">
          <p class="text-sm font-medium">Income Verification Documents</p>
          <div class="space-y-1.5">
            <div
              v-for="doc in INCOME_DOCS"
              :key="doc"
              class="hover:bg-accent flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1"
              @click="income[doc] = !income[doc]"
            >
              <Checkbox
                :id="`psa-income-${doc}`"
                v-model="income[doc]"
                class="mt-0.5 shrink-0"
              />
              <Label :for="`psa-income-${doc}`" class="cursor-pointer text-sm font-normal leading-snug">
                {{ doc }}
              </Label>
            </div>
          </div>
        </div>

        <Separator />

        <!-- Liability documents -->
        <div class="space-y-2">
          <p class="text-sm font-medium">Liabilities &amp; Expense Verification Documents</p>
          <div class="space-y-1.5">
            <div
              v-for="doc in LIABILITY_DOCS"
              :key="doc"
              class="hover:bg-accent flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1"
              @click="liability[doc] = !liability[doc]"
            >
              <Checkbox
                :id="`psa-liability-${doc}`"
                v-model="liability[doc]"
                class="mt-0.5 shrink-0"
              />
              <Label :for="`psa-liability-${doc}`" class="cursor-pointer text-sm font-normal leading-snug">
                {{ doc }}
              </Label>
            </div>
          </div>
        </div>
      </div>

      <p v-if="!hasSelection()" class="text-xs text-amber-600">
        Select at least one document from each section.
      </p>

      <DialogFooter>
        <Button
          :disabled="!hasSelection() || processing"
          class="w-full"
          @click="submit"
        >
          {{ processing ? 'Generating…' : 'Generate PSA' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
